import { useState, useEffect } from "react";
import { useToast } from "../../hooks/use-toast";
import api from "../../lib/api";
import { useAuth } from "../../lib/authContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import {
  X,
  Send,
  Phone,
  MessageSquare,
  Clock,
  CheckCircle,
  User,
  Calendar,
  Package,
  AlertCircle,
  XCircle,
  Star,
  PhoneCall,
  FileText,
} from "lucide-react";

interface SupportCase {
  id: string;
  case_number: string;
  order_id: string | null;
  customer_id: string;
  topic: string;
  subject: string;
  description: string | null;
  status: string;
  priority: string;
  assigned_to: string | null;
  department: string;
  callback_requested: boolean;
  callback_scheduled_at: string | null;
  callback_completed_at: string | null;
  callback_attempts: number;
  chatwoot_conversation_id: number | null;
  chatwoot_contact_id: number | null;
  resolution_rating: number | null;
  resolution_feedback: string | null;
  tags: string[] | null;
  metadata: any;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  closed_at: string | null;
  first_viewed_at?: string | null;
  // New fields from joins
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  tracking_id?: string;
}

interface SupportMessage {
  id: string;
  case_id: string;
  message_type: string;
  sender_type: string;
  sender_id: string | null;
  content: string;
  metadata: any;
  step_id: string | null;
  requires_response: boolean;
  is_internal: boolean;
  chatwoot_message_id: number | null;
  created_at: string;
  updated_at: string;
}

interface Props {
  supportCase: SupportCase;
  onClose: () => void;
  onUpdate: () => void;
}

export default function SupportCaseModal({
  supportCase,
  onClose,
  onUpdate,
}: Props) {
  const [caseData, setCaseData] = useState<SupportCase>(supportCase);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = "hidden";

    return () => {
      // Re-enable body scroll when modal is closed
      document.body.style.overflow = "unset";
    };
  }, []);

  useEffect(() => {
    loadMessages();
  }, [supportCase.id]);

  const loadMessages = async () => {
    try {
      setIsLoadingMessages(true);
      const messages = await api.getSupportMessages(supportCase.id);
      setMessages(messages || []);
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to load messages",
        variant: "destructive",
      });
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleReopen = async () => {
    try {
      setIsLoading(true);
      const response = await api.updateSupportCase(caseData.id, {
        status: "open",
        closed_at: null,
        resolved_at: null,
      });

      if (response && !response.error) {
        setCaseData({
          ...caseData,
          status: "open",
          closed_at: null,
          resolved_at: null,
        });
        toast({
          title: "Success",
          description: "Case reopened successfully",
        });
        onUpdate();
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to reopen case",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseCase = async () => {
    try {
      setIsLoading(true);
      const response = await api.updateSupportCase(caseData.id, {
        status: "closed",
        closed_at: new Date().toISOString(),
      });

      if (response && !response.error) {
        setCaseData({
          ...caseData,
          status: "closed",
          closed_at: new Date().toISOString(),
        });
        toast({
          title: "Success",
          description: "Case closed successfully",
        });
        onUpdate();
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to close case",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setIsLoading(true);
      const response = await api.createSupportMessage(caseData.id, {
        message_type: "text",
        sender_type: "agent",
        sender_id: user?.id || "system",
        content: newMessage.trim(),
        is_internal: false,
      });

      if (response && !response.error) {
        toast({
          title: "Success",
          description: "Message sent successfully",
        });
        setNewMessage("");
        loadMessages();
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to send message",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      open: AlertCircle,
      in_progress: Clock,
      resolved: CheckCircle,
      closed: XCircle,
    };
    return icons[status as keyof typeof icons] || AlertCircle;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      open: "text-blue-600",
      in_progress: "text-yellow-600",
      resolved: "text-green-600",
      closed: "text-gray-600",
    };
    return colors[status as keyof typeof colors] || "text-gray-600";
  };

  const formatDate = (date: string | null) => {
    if (!date) return "-";
    return new Date(date).toLocaleString();
  };

  const getSenderBadge = (message: SupportMessage) => {
    if (message.sender_type === "customer") {
      return <Badge className="bg-blue-100 text-blue-800">Customer</Badge>;
    } else if (message.sender_type === "agent") {
      return <Badge className="bg-green-100 text-green-800">Agent</Badge>;
    } else if (message.sender_type === "system") {
      return <Badge className="bg-gray-100 text-gray-800">System</Badge>;
    }
    return null;
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={e => {
        // Close modal if clicking on backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-6xl h-[90vh] flex flex-col relative"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b dark:border-gray-700 flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold dark:text-white flex items-center gap-2">
              <MessageSquare size={24} />
              Case #{caseData.case_number}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {caseData.subject}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex min-h-0">
          {/* Messages Section */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0 max-h-full">
              {isLoadingMessages ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <MessageSquare
                    size={48}
                    className="mx-auto mb-4 text-gray-400"
                  />
                  <p>No messages yet</p>
                </div>
              ) : (
                messages.map(message => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.sender_type === "customer"
                        ? "justify-start"
                        : "justify-end"
                    }`}
                  >
                    <div
                      className={`max-w-[70%] ${
                        message.sender_type === "customer"
                          ? "bg-gray-100 dark:bg-gray-800"
                          : "bg-blue-100 dark:bg-blue-900"
                      } rounded-lg p-4`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        {getSenderBadge(message)}
                        <span className="text-xs text-gray-500">
                          {formatDate(message.created_at)}
                        </span>
                      </div>
                      <p className="text-sm dark:text-gray-200">
                        {message.content}
                      </p>
                      {message.is_internal && (
                        <Badge variant="outline" className="mt-2 text-xs">
                          Internal Note
                        </Badge>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Message Input */}
            <div className="border-t dark:border-gray-700 p-4">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  onKeyPress={e => e.key === "Enter" && handleSendMessage()}
                  disabled={isLoading}
                />
                <Button onClick={handleSendMessage} disabled={isLoading}>
                  <Send size={16} className="mr-1" />
                  Send
                </Button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-96 border-l dark:border-gray-700 p-6 space-y-6 overflow-y-auto min-h-0">
            {/* Quick Info Section */}
            <div>
              <h3 className="font-medium mb-4">Quick Info</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status</span>
                  <div
                    className={`flex items-center gap-1 ${getStatusColor(
                      caseData.status,
                    )}`}
                  >
                    {(() => {
                      const Icon = getStatusIcon(caseData.status);
                      return <Icon size={16} />;
                    })()}
                    <span className="font-medium">
                      {caseData.status
                        .replace("_", " ")
                        .charAt(0)
                        .toUpperCase() +
                        caseData.status.slice(1).replace("_", " ")}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Priority</span>
                  <Badge
                    className={
                      caseData.priority === "urgent"
                        ? "bg-red-500 text-white"
                        : caseData.priority === "high"
                        ? "bg-red-100 text-red-800"
                        : caseData.priority === "medium"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-gray-100 text-gray-800"
                    }
                  >
                    {caseData.priority.charAt(0).toUpperCase() +
                      caseData.priority.slice(1)}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Customer Details */}
            <div className="pt-4 border-t dark:border-gray-700">
              <h3 className="font-medium mb-4">Customer Details</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <User size={16} className="text-gray-500" />
                    <span className="font-medium">
                      {caseData.customer_name || "Unknown"}
                    </span>
                  </div>
                  {caseData.customer_phone && (
                    <div className="flex items-center gap-2 ml-6 text-sm text-gray-600">
                      <Phone size={14} />
                      <span>{caseData.customer_phone}</span>
                    </div>
                  )}
                  {caseData.customer_email && (
                    <div className="ml-6 text-sm text-gray-600">
                      {caseData.customer_email}
                    </div>
                  )}
                </div>
                {caseData.tracking_id && (
                  <div>
                    <div className="flex items-center gap-2">
                      <Package size={16} className="text-gray-500" />
                      <span className="text-sm">
                        <span className="text-gray-600">Tracking:</span>{" "}
                        <span className="font-medium">
                          {caseData.tracking_id}
                        </span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Case Details */}
            <div className="pt-4 border-t dark:border-gray-700">
              <h3 className="font-medium mb-4">Case Details</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-600">Topic</span>
                  <p className="font-medium">
                    {caseData.topic.replace("_", " ").charAt(0).toUpperCase() +
                      caseData.topic.slice(1).replace("_", " ")}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm text-gray-600 mb-2">Description</h3>
                  <p className="text-sm text-black">{caseData.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-600">Created</span>
                    <p className="text-sm font-medium">
                      {formatDate(caseData.created_at).split(",")[0]}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Updated</span>
                    <p className="text-sm font-medium">
                      {formatDate(caseData.updated_at).split(",")[0]}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">FirstViewedAt</span>
                    <p className="text-sm font-medium">
                      {caseData.first_viewed_at ? formatDate(caseData.first_viewed_at) : "-"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">ClosedAt</span>
                    <p className="text-sm font-medium">
                      {caseData.closed_at ? formatDate(caseData.closed_at) : "-"}
                    </p>
                  </div>
                </div>
                {caseData.tags && caseData.tags.length > 0 && (
                  <div>
                    <span className="text-sm text-gray-600">Tags</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {caseData.tags.map((tag, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="text-xs"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Callback Info */}
            {caseData.callback_requested && (
              <div className="pt-4 border-t dark:border-gray-700">
                <h3 className="font-medium flex items-center gap-2 mb-3">
                  <Phone size={16} />
                  Callback Information
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Scheduled</span>
                    <span className="font-medium">
                      {formatDate(caseData.callback_scheduled_at).split(",")[0]}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Attempts</span>
                    <span className="font-medium">
                      {caseData.callback_attempts}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status</span>
                    <Badge
                      variant={
                        caseData.callback_completed_at ? "default" : "outline"
                      }
                    >
                      {caseData.callback_completed_at ? "Completed" : "Pending"}
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            {/* Resolution Feedback */}
            {caseData.resolution_rating && (
              <div className="pt-4 border-t dark:border-gray-700">
                <h3 className="font-medium flex items-center gap-2 mb-3">
                  <Star size={16} />
                  Resolution Feedback
                </h3>
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className={
                        i < caseData.resolution_rating!
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-400"
                      }
                    />
                  ))}
                  <span className="ml-2 text-sm">
                    {caseData.resolution_rating}/5
                  </span>
                </div>
                {caseData.resolution_feedback && (
                  <p className="text-sm text-gray-600">
                    {caseData.resolution_feedback}
                  </p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="pt-4 border-t dark:border-gray-700">
              <h3 className="font-medium mb-3">Actions</h3>
              <div className="space-y-2">
                {caseData.status === "closed" ? (
                  <Button
                    onClick={handleReopen}
                    disabled={isLoading}
                    className="w-full"
                    variant="outline"
                  >
                    Reopen Case
                  </Button>
                ) : (
                  <Button
                    onClick={handleCloseCase}
                    disabled={isLoading}
                    className="w-full"
                    variant="outline"
                  >
                    Close Case
                  </Button>
                )}

                {caseData.chatwoot_conversation_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      window.open(
                        `https://app.chatwoot.com/app/accounts/1/conversations/${caseData.chatwoot_conversation_id}`,
                        "_blank",
                      );
                    }}
                  >
                    <MessageSquare size={16} className="mr-2" />
                    View in Chatwoot
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
